import consoleProxy from "console-proxy"
import { RingBuffer } from "ring-buffer-ts"

const CONSOLE_LEVELS = ["log", "warn", "error", "info"] as const
type ConsoleLevel = (typeof CONSOLE_LEVELS)[number]

interface StackInfo {
  file?: string
  line?: number
  column?: number
  function?: string
}

interface LogEntry {
  level: ConsoleLevel
  message: unknown[]
  timestamp: Date
  stack?: StackInfo
}

declare global {
  interface Console {
    getBuffer(): LogEntry[]
    clearBuffer(): void
  }
}

const maxBufferSize = 100
const buffer = new RingBuffer<LogEntry>(maxBufferSize)

function parseStackTrace(): StackInfo | undefined {
  const { stack } = new Error()
  if (!stack) return undefined

  // Split stack trace into lines
  const lines = stack.split("\n")

  // Find the first line that's not from this file (console-interceptor.ts) or console-proxy
  // Typically: line 0 is "Error", line 1 is parseStackTrace, line 2 is addToBuffer, line 3 is the hook, line 4 is the actual caller
  for (let i = 3; i < lines.length; i++) {
    const line = lines[i]
    if (!line || line.includes("console-interceptor") || line.includes("console-proxy")) {
      continue
    }

    // Chrome/Edge format: "    at functionName (file:line:column)" or "    at file:line:column"
    const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)/)
    if (match) {
      return {
        function: match[1]?.trim(),
        file: match[2],
        line: parseInt(match[3], 10),
        column: parseInt(match[4], 10),
      }
    }
  }

  return undefined
}

function addToBuffer(level: ConsoleLevel, message: unknown[], stack?: StackInfo): void {
  buffer.add({
    level,
    message,
    timestamp: new Date(),
    stack,
  })
}

function getBuffer(): LogEntry[] {
  return buffer.toArray()
}

function clearBuffer(): void {
  buffer.clear()
}

function exposeBufferMethods(): void {
  const extendedConsole = window.console
  extendedConsole.getBuffer = getBuffer
  extendedConsole.clearBuffer = clearBuffer
}

function hookConsoleMethods(): void {
  const consoleConfig: Record<string, (...args: unknown[]) => unknown[]> = {}

  CONSOLE_LEVELS.forEach((level) => {
    consoleConfig[level] = function (...args: unknown[]): unknown[] {
      const stack = parseStackTrace()
      addToBuffer(level, args, stack)

      // Format stack info for console output
      if (stack) {
        const stackInfo = `[${stack.function || "anonymous"} @ ${stack.file}:${stack.line}:${stack.column}]`
        return [...args, stackInfo]
      }

      return args
    }
  })

  window.console = consoleProxy.getConsole(consoleConfig)
  exposeBufferMethods()
}

window.addEventListener("error", (event) => {
  addToBuffer("error", [event.message, event.filename, event.lineno, event.colno, event.error])
  return false
})

window.addEventListener("unhandledrejection", (event) => {
  addToBuffer("error", [event.reason])
  return false
})

hookConsoleMethods()
